import { jsPDF } from 'jspdf';
import * as XLSX from '@redoper1/xlsx-js-style';

const text = (value) => String(value ?? '').trim();
const formatDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : String(value);
};
const safeFilename = (value) => text(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9._-]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || 'planos-de-aula';

const planItems = (plan) => {
  const fallbackNucleusId = plan?.nucleusId || null;
  const fallbackNucleusName = plan?.nucleusName || '';
  const source = Array.isArray(plan?.items) && plan.items.length
    ? plan.items
    : [{ lessonTheme: plan?.lessonTheme || '', objective: plan?.objective || '' }];
  return source.map((item) => {
    const hasTopicNucleus = Object.prototype.hasOwnProperty.call(item || {}, 'nucleusId') || Object.prototype.hasOwnProperty.call(item || {}, 'nucleusName');
    return {
      ...item,
      nucleusId: hasTopicNucleus ? (item?.nucleusId || null) : fallbackNucleusId,
      nucleusName: hasTopicNucleus ? (item?.nucleusName || '') : fallbackNucleusName,
    };
  });
};

const planStatus = (plan, today) => {
  if (plan?.endDate && plan.endDate < today) return 'Concluído';
  if (plan?.startDate && plan.startDate > today) return 'Próximo';
  return 'Em andamento';
};

const audienceText = (plan, classes, parents, parentLabel) => {
  const classNames = (plan?.classIds || [])
    .map((id) => classes.find((item) => Number(item.id) === Number(id))?.name)
    .filter(Boolean);
  const parentNames = (plan?.parentIds || [])
    .map((id) => parents.find((item) => Number(item.id) === Number(id))?.name)
    .filter(Boolean);

  if (classNames.length) {
    const parts = [`Turmas: ${classNames.join(', ')}`];
    if (parentNames.length) parts.push(`${parentLabel}: ${parentNames.join(', ')}`);
    return parts.join(' | ');
  }
  if (parentNames.length) return `Todas as turmas de ${parentLabel.toLowerCase()}: ${parentNames.join(', ')}`;
  return 'Todas as turmas';
};

// Minutes from the server are authoritative; HA values are never reconstructed from diaries.
const numeric = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;
const numberText = (value) => numeric(value) === null ? 'Não informado' : new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value);
const minutesText = (value) => numeric(value) === null ? 'Não informado' : `${numberText(value)} min`;
const timing = (source, plan) => {
  const plannedMinutes = numeric(source?.plannedMinutes);
  const actualMinutes = numeric(source?.actualMinutes);
  return {
    lessonHourMinutes: numeric(plan.lessonHourMinutes),
    plannedHours: numeric(source?.plannedHours),
    plannedMinutes,
    actualMinutes,
    actualLessonHours: numeric(source?.actualLessonHours),
    differenceMinutes: plannedMinutes !== null && actualMinutes !== null ? actualMinutes - plannedMinutes : null,
    sessionsWithoutDuration: numeric(source?.sessionsWithoutDuration),
    recordedSessions: numeric(source?.recordedSessions),
  };
};
const timingLines = (row) => [
  `Unidade: ${row.lessonHourMinutes === null ? 'HA sem duração informada' : `1 HA = ${numberText(row.lessonHourMinutes)} min`} | HA previstas: ${numberText(row.plannedHours)} | HA realizadas: ${numberText(row.actualLessonHours)}`,
  `Tempo previsto: ${minutesText(row.plannedMinutes)} | Tempo utilizado: ${minutesText(row.actualMinutes)} | Diferença (utilizado - previsto): ${minutesText(row.differenceMinutes)}`,
  `Sessões registradas: ${numberText(row.recordedSessions)} | Sessões sem duração: ${numberText(row.sessionsWithoutDuration)}${row.sessionsWithoutDuration > 0 ? ' — tempo realizado parcial (somente durações conhecidas)' : ''}`,
];

export const lessonPlanReportRows = ({ plans = [], classes = [], parents = [], parentLabel = 'Cursos', nucleusId = '' }) => {
  const today = new Date().toISOString().slice(0, 10);
  return plans.flatMap((plan) => [
    ...planItems(plan).map((item) => ({ ...item, orphan: false })),
    ...(plan.timeSummary?.orphanTopics || []).map((item) => ({ ...item, orphan: true })),
  ]
    .filter((item) => !nucleusId || String(item.nucleusId || '') === String(nucleusId))
    .map((item, index) => ({
      period: `${formatDate(plan.startDate)} a ${formatDate(plan.endDate)}`,
      responsible: text(plan.responsibleName) || 'Não informado',
      audience: audienceText(plan, classes, parents, parentLabel),
      theme: `${text(item.lessonTheme) || '—'}${item.orphan ? ' (tema histórico removido)' : ''}`,
      objective: text(item.objective) || '—',
      status: planStatus(plan, today),
      nucleus: text(item.nucleusName),
      topicNumber: index + 1,
      topicId: item.id,
      orphan: item.orphan,
      ...timing(item, plan),
    })));
};

// These are whole-plan totals, including historical topics, even when topic rows are filtered.
export const lessonPlanReportTotals = ({ plans = [] }) => plans.map((plan) => ({
  period: `${formatDate(plan.startDate)} a ${formatDate(plan.endDate)}`,
  responsible: text(plan.responsibleName) || 'Não informado',
  ...timing(plan.timeSummary, plan),
  ...(plan.timeSummary?.topicsWithoutPlannedHours > 0 ? { differenceMinutes: null } : {}),
  topicsWithoutPlannedHours: numeric(plan.timeSummary?.topicsWithoutPlannedHours),
  unlinkedSessions: numeric(plan.timeSummary?.unlinkedSessions),
  unlinkedSessionsWithoutDuration: numeric(plan.timeSummary?.unlinkedSessionsWithoutDuration),
}));

export function downloadLessonPlanPdf(options = {}) {
  const { plans = [], includeNucleus = false } = options;
  const rows = lessonPlanReportRows(options);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const margin = 10;
  const footerY = height - 8;
  const drawHeader = () => {
    doc.setFillColor(10, 47, 103);
    doc.rect(0, 0, width, 27, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('RELATÓRIO DE PLANOS DE AULA', margin, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`${rows.length} tema(s) em ${plans.length} plano(s) • HA conforme unidade de cada plano`, margin, 20);
    return 34;
  };
  let y = drawHeader();
  // Paginate individual text lines so even an objective longer than a page is preserved.
  const writeBlock = (lines, heading) => {
    doc.setFontSize(8);
    const wrapped = lines.flatMap((line) => doc.splitTextToSize(line, width - margin * 2 - 4));
    if (y + Math.min(wrapped.length + 2, 8) * 4 > footerY - 5) {
      doc.addPage('a4', 'landscape');
      y = drawHeader();
    }
    const headingLines = doc.splitTextToSize(heading, width - margin * 2 - 4);
    [...headingLines, ...wrapped].forEach((line, index) => {
      if (y + 4 > footerY - 5) {
        doc.addPage('a4', 'landscape');
        y = drawHeader();
      }
      doc.setTextColor(31, 58, 91);
      doc.setFontSize(8);
      doc.setFont('helvetica', index < headingLines.length ? 'bold' : 'normal');
      doc.text(line, margin + 2, y);
      y += 4;
    });
    doc.setDrawColor(218, 228, 240);
    doc.line(margin, y, width - margin, y);
    y += 6;
  };
  rows.forEach((row, index) => writeBlock([
    `Período: ${row.period} | Responsável: ${row.responsible} | Status: ${row.status}`,
    `Aplicação: ${row.audience}${includeNucleus && row.nucleus ? ` | Núcleo: ${row.nucleus}` : ''}`,
    `Objetivo: ${row.objective}`,
    ...timingLines(row),
  ], `Tema ${index + 1}: ${row.theme}`));
  lessonPlanReportTotals({ plans }).forEach((row, index) => writeBlock([
    `Período: ${row.period} | Responsável: ${row.responsible}`,
    ...timingLines(row),
    ...(row.topicsWithoutPlannedHours > 0 ? [`Atenção: previsão parcial — ${numberText(row.topicsWithoutPlannedHours)} tema(s) sem HA previstas. Os totais previstos incluem somente temas com previsão; diferença não informada.`] : []),
    ...(row.unlinkedSessions > 0 ? [`Atenção: ${numberText(row.unlinkedSessions)} sessão(ões) sem vínculo com plano, sendo ${numberText(row.unlinkedSessionsWithoutDuration)} sem duração. Esses registros não compõem o tempo realizado deste plano.`] : []),
  ], `Total do plano ${index + 1} (todos os temas, incluindo históricos; sem filtro de núcleo)`));
  if (!rows.length) writeBlock([], 'Nenhum tema encontrado para os filtros atuais.');
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(218, 228, 240);
    doc.line(margin, footerY - 3, width - margin, footerY - 3);
    doc.setTextColor(105, 123, 145);
    doc.setFontSize(6.5);
    doc.text('Relatório de Planos de Aula • iBPM Nash', margin, footerY);
    doc.text(`Página ${page} de ${pages}`, width - margin, footerY, { align: 'right' });
  }
  doc.save(`${safeFilename('relatorio-planos-de-aula')}.pdf`);
}

const timingColumns = (row) => ({
  'Minutos por HA': row.lessonHourMinutes ?? 'Não informado',
  'HA previstas': row.plannedHours ?? 'Não informado',
  'HA realizadas': row.actualLessonHours ?? 'Não informado',
  'Tempo previsto (min)': row.plannedMinutes ?? 'Não informado',
  'Tempo utilizado (min)': row.actualMinutes ?? 'Não informado',
  'Diferença utilizado - previsto (min)': row.differenceMinutes ?? 'Não informado',
  'Sessões registradas': row.recordedSessions ?? 'Não informado',
  'Sessões sem duração': row.sessionsWithoutDuration ?? 'Não informado',
  'Apuração do tempo': row.sessionsWithoutDuration > 0 ? 'Parcial: há sessões sem duração' : numeric(row.actualMinutes) === null ? 'Não informado' : 'Durações conhecidas',
});

export function downloadLessonPlanXlsx({ plans = [], classes = [], parents = [], parentLabel = 'Cursos', includeNucleus = false, nucleusId = '' }) {
  const rows = lessonPlanReportRows({ plans, classes, parents, parentLabel, nucleusId });
  const showNucleus = Boolean(includeNucleus && rows.some((row) => row.nucleus));
  const data = rows.map((row) => ({
    'Período': row.period,
    'Responsável': row.responsible,
    ...(showNucleus ? { 'Núcleo': row.nucleus || '—' } : {}),
    'Aplicação': row.audience,
    'Tema': row.theme,
    'Objetivo': row.objective,
    'Status': row.status,
    ...timingColumns(row),
  }));

  const worksheet = XLSX.utils.json_to_sheet(data.length ? data : [{
    'Período': '',
    'Responsável': '',
    ...(showNucleus ? { 'Núcleo': '' } : {}),
    'Aplicação': '',
    'Tema': '',
    'Objetivo': '',
    'Status': '',
    ...timingColumns({}),
  }]);
  worksheet['!cols'] = showNucleus
    ? [{ wch: 23 }, { wch: 26 }, { wch: 28 }, { wch: 42 }, { wch: 36 }, { wch: 48 }, { wch: 16 }]
    : [{ wch: 23 }, { wch: 28 }, { wch: 48 }, { wch: 38 }, { wch: 50 }, { wch: 16 }];

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  for (let col = range.s.c; col <= range.e.c; col += 1) {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
    if (!cell) continue;
    cell.s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '0A2F67' } },
      alignment: { vertical: 'center' },
    };
  }
  for (let row = 1; row <= range.e.r; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (!cell) continue;
      cell.s = {
        alignment: { vertical: 'top', wrapText: true },
        fill: row % 2 === 0 ? { fgColor: { rgb: 'F7FAFE' } } : undefined,
      };
    }
  }

  const timingColumnStart = showNucleus ? 7 : 6;
  for (let col = timingColumnStart; col < timingColumnStart + 9; col += 1) {
    worksheet['!cols'][col] = { wch: 25 };
    for (let row = 1; row <= range.e.r; row += 1) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (cell?.t === 'n') cell.z = col === timingColumnStart + 1 || col === timingColumnStart + 2 ? '0.00' : '0.########';
    }
  }
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Planos de Aula');
  const totals = lessonPlanReportTotals({ plans }).map((row, index) => ({
    'Plano': index + 1,
    'Período': row.period,
    'Responsável': row.responsible,
    'Escopo': 'Plano completo, incluindo temas históricos; sem filtro de núcleo',
    ...timingColumns(row),
    'Temas sem HA previstas': row.topicsWithoutPlannedHours ?? 'Não informado',
    'Apuração da previsão': row.topicsWithoutPlannedHours > 0 ? 'Parcial: há temas sem HA previstas' : row.plannedMinutes === null ? 'Não informado' : 'Previsões conhecidas',
    'Sessões sem vínculo (não incluídas)': row.unlinkedSessions ?? 'Não informado',
    'Sessões sem vínculo e sem duração': row.unlinkedSessionsWithoutDuration ?? 'Não informado',
  }));
  const totalsSheet = XLSX.utils.json_to_sheet(totals);
  totalsSheet['!cols'] = Array.from({ length: 17 }, () => ({ wch: 25 }));
  // HA are rounded only by spreadsheet display formatting; numeric values retain precision.
  totals.forEach((row, index) => {
    [5, 6].forEach((col) => {
      const cell = totalsSheet[XLSX.utils.encode_cell({ r: index + 1, c: col })];
      if (cell?.t === 'n') cell.z = '0.00';
    });
  });
  XLSX.utils.book_append_sheet(workbook, totalsSheet, 'Totais dos planos');
  XLSX.writeFile(workbook, `${safeFilename('relatorio-planos-de-aula')}.xlsx`);
}
